import getVideoId from "get-video-id";
import VideoGate from "./VideoGate";

// Re-exported from a cycle-free module: `VideoGate` also needs it, and
// importing it straight from here would create a `Video ↔ VideoGate` import
// cycle. Other consumers (e.g. the PeerTube embed) keep importing from
// `Video.jsx`.
export { responsiveIFrameContainer } from "./ResponsiveIFrame.jsx";

// Returns a gate descriptor for a German mediathek (ARD/ZDF/arte) embed, or null.
// `id` (and `lang` for arte) is the opaque provider id the poster endpoint needs;
// `embedSrc` is the iframe URL loaded on click, `autoplayParam` is appended then.
const matchMediathekEmbed = (url) => {
  let m;
  m = url.match(/ardmediathek\.de\/video\/[^/]+\/[^/]+\/[^/]+\/([^/?#]+)/);
  if (m)
    return {
      service: "ardmediathek",
      id: m[1],
      embedSrc: `https://www.ardmediathek.de/embed/${m[1]}`,
      allow: "autoplay; fullscreen; clipboard-read; clipboard-write",
      // ARD registers an `autoplay` embed param but doesn't reliably honor it
      // from the URL — best-effort, may need one extra click inside the player.
      autoplayParam: "autoplay=true",
    };
  m = url.match(/zdf\.de\/play\/[^/]+\/[^/]+\/([^/?#]+)/);
  if (m)
    return {
      service: "zdf",
      id: m[1],
      embedSrc: `https://ngp.zdf.de/miniplayer/embed/?mediaID=${m[1]}`,
      allow: "autoplay; fullscreen",
      // ZDF's ngp miniplayer reads the camelCase `autoPlay` query param.
      autoplayParam: "autoPlay=true",
    };
  m = url.match(/arte\.tv\/([a-z]{2})\/videos\/([^/?#]+)/);
  if (m)
    return {
      service: "arte",
      id: m[2],
      lang: m[1],
      embedSrc: `https://www.arte.tv/embeds/${m[1]}/${m[2]}`,
      allow: "autoplay; fullscreen",
      autoplayParam: "autoplay=1",
    };
  return null;
};

// Unlisted Vimeo videos carry a privacy hash, either as `?h=<hash>` on a player
// URL or as the second path segment of `vimeo.com/<id>/<hash>`. Both the player
// embed (401 without it) and the oEmbed poster lookup (200, but no
// `thumbnail_url`) need it, so it has to travel with the id.
const getVimeoHash = (url) =>
  url.match(/[?&]h=([0-9a-zA-Z]+)/)?.[1] ??
  url.match(/vimeo\.com\/\d+\/([0-9a-zA-Z]+)/)?.[1] ??
  undefined;

/**
 * VideoEmbed is a React functional component used to render an embedded video player for various video services.
 * It takes a video URL as input and determines the appropriate video platform to generate an embeddable iframe.
 * The component supports the popular video services Facebook, Vimeo, and YouTube.
 *
 * @param {object} props - Component properties.
 * @param {string} props.url - The URL of the video to be embedded. This should be a fully-qualified URL for the desired video.
 * @returns {JSX.Element} - A React element containing the responsive iframe for the video or a message indicating an error or unsupported service.
 */
const VideoEmbed = ({ url }) => {
  if (!url) return <div>No video url given</div>;

  // Only an explicit `#ratio:` is authoritative. An embed snippet's iframe
  // `width`/`height` are boilerplate that routinely disagree with the video —
  // Vimeo ships `width="640" height="360"` for clips that are actually 640x1138
  // — so they're just a starting guess that the measured poster may override.
  const getWidthHeight = () => {
    const ratioMatch = url.match(/#ratio:(\d+)x(\d+)/);
    if (ratioMatch)
      return [parseInt(ratioMatch[1], 10), parseInt(ratioMatch[2], 10), false];
    const widthMatch = url.match(/width="?(\d+)/i);
    const heightMatch = url.match(/height="?(\d+)/i);
    if (widthMatch && heightMatch) {
      return [parseInt(widthMatch[1]), parseInt(heightMatch[1]), true];
    }
    return [16, 9, true];
  };

  const [w, h, ratioIsFallback] = getWidthHeight();
  const ratio = h / w;

  // Every external embed funnels through the same click-to-play gate, so no
  // third-party request fires until the user explicitly clicks (privacy/GDPR).
  const mediathek = matchMediathekEmbed(url);
  if (mediathek) {
    return (
      <VideoGate
        ratio={ratio}
        ratioIsFallback={ratioIsFallback}
        {...mediathek}
      />
    );
  }

  const { id, service } = getVideoId(url);

  if (!id) return <div>Unkown Video ID</div>;

  if (service === "vimeo") {
    const hash = getVimeoHash(url);
    return (
      <VideoGate
        service="vimeo"
        id={id}
        hash={hash}
        ratio={ratio}
        ratioIsFallback={ratioIsFallback}
        embedSrc={`https://player.vimeo.com/video/${id}?dnt=1${
          hash ? `&h=${hash}` : ""
        }`}
        allow="autoplay; fullscreen"
        autoplayParam="autoplay=1"
      />
    );
  }

  if (service === "youtube") {
    return (
      <VideoGate
        service="youtube"
        id={id}
        ratio={ratio}
        ratioIsFallback={ratioIsFallback}
        embedSrc={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        autoplayParam="autoplay=1"
      />
    );
  }
  return <div>Unkown source</div>;
};

export default VideoEmbed;
